import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyB3pveQUwt9iTkR7r5eCUV-3aLL-DljQKY",
  authDomain: "exator-1526c.firebaseapp.com",
  projectId: "exator-1526c",
  storageBucket: "exator-1526c.firebasestorage.app",
  messagingSenderId: "939475171283",
  appId: "1:939475171283:web:0f1319cd043e2503e5a126"
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
// Always show Google's account chooser, even if the user is already signed into one Google account in the browser.
// Without this, the popup would silently auto-pick the only logged-in Google account and skip the picker entirely.
googleProvider.setCustomParameters({ prompt: "select_account" });

// Sign in with Apple — required by Apple for App Store approval when a third-party social
// sign-in (Google) is offered. Requires a one-time setup in Firebase Console:
// 1) Apple Developer account → Identifiers → create a Services ID (e.g. com.exator.web)
// 2) Configure "Sign in with Apple" capability on the Services ID, add domain (exator.app)
//    and return URL (https://exator-1526c.firebaseapp.com/__/auth/handler)
// 3) Create a Sign-in with Apple key, download the .p8 file
// 4) Firebase Console → Auth → Sign-in method → Apple → enable, paste Services ID + Team ID
//    + Key ID + paste the .p8 contents
export const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");
export const db = getFirestore(app);
// Storage holds profile avatars under avatars/{uid}/*. Bucket is the same project's default.
export const storage = getStorage(app);

// ── Firebase Cloud Messaging (push notifications) ─────────────────────────────
// Web push needs a VAPID key pair. Generate it ONCE in:
//   Firebase Console → Project settings → Cloud Messaging → Web configuration →
//   "Web Push certificates" → Generate key pair → copy the key string.
// Put it in .env.local + GitHub secret as REACT_APP_FIREBASE_VAPID_KEY.
// Without it, getFcmToken() no-ops (no crash) and only in-app notifications work.
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

// Messaging isn't supported in every browser (Safari < 16.4, some in-app webviews).
// isSupported() is async; we resolve a singleton promise so callers can await it safely.
let _messaging = null;
const messagingReady = (async () => {
  try {
    if (await isSupported()) {
      _messaging = getMessaging(app);
      return _messaging;
    }
  } catch (e) {
    console.warn("[fcm] messaging not supported here:", e?.message);
  }
  return null;
})();

// Request/refresh the FCM registration token for this device. Returns the token string
// or null. The caller (App.js) saves it to the user doc so Cloud Functions can target it.
export async function getFcmToken() {
  if (!VAPID_KEY) {
    console.log("[fcm] no VAPID key configured — skipping token registration");
    return null;
  }
  const m = await messagingReady;
  if (!m) return null;
  try {
    // FCM needs the dedicated /firebase-messaging-sw.js service worker registered.
    const swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
    return token || null;
  } catch (e) {
    console.warn("[fcm] getToken failed:", e?.message);
    return null;
  }
}

// Foreground message handler — fires when a push arrives while the app tab is focused
// (background pushes are handled by firebase-messaging-sw.js). Caller passes a callback
// to surface it as an in-app toast.
export async function onForegroundMessage(cb) {
  const m = await messagingReady;
  if (!m) return () => {};
  return onMessage(m, cb);
}

console.log("✅ Firebase connected");